<template>
  <div
    ref="popover"
    class="ndsPopover"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div
      ref="popoverTrigger"
      class="ndsPopover__trigger"
      data-testid="nds-popover-trigger"
      @click.stop="handleClickOnTrigger"
    >
      <slot name="trigger" />
    </div>

    <div
      v-show="show"
      ref="popoverContent"
      class="ndsPopover__content"
      data-testid="nds-popover-content"
      @click.stop="handleClickOnPopover"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <slot name="popover" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, type PropType } from 'vue'
import Popper, { type Modifiers, type PopperOptions } from 'popper.js'
import { onClickOutside } from '@vueuse/core'

import { NdsPopoverPlacement } from '@/components/NewDesignSystem/NdsPopover/NdsPopover.interfaces'

const emit = defineEmits<{
  (e: 'open'): void
  (e: 'close'): void
}>()

const props = defineProps({
  closeOnContentClick: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  doTeleport: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  placement: {
    type: String as PropType<NdsPopoverPlacement>,
    default: NdsPopoverPlacement.AUTO,
  },
  modifiers: {
    type: Object as PropType<Modifiers>,
    default: () => {},
  },
  openOnHover: {
    type: Boolean,
    default: false,
  },
})

const show = ref(false)
const popper = ref<Popper>()

const popover = ref<HTMLElement | null>(null)
const popoverTrigger = ref<HTMLElement | null>(null)
const popoverContent = ref<HTMLElement | null>(null)

const options = ref<PopperOptions>({
  positionFixed: true,
  placement: props.placement,
  modifiers: {
    flip: { enabled: true, behavior: 'flip' },
    preventOverflow: {
      enabled: true,
      boundariesElement: 'viewport',
    },
    ...props.modifiers,
  },
})

const moveToBody = () => {
  if (popoverContent.value) {
    document.body.appendChild(popoverContent.value)
  }
}

const moveToContainer = () => {
  if (popoverContent.value && popoverContent.value.parentNode === document.body) {
    popover.value?.appendChild(popoverContent.value)
  }
}

const removeTeleportedPopperContent = () => {
  if (popoverContent.value?.parentNode === document.body) {
    popoverContent.value.remove()
  }
}

const destroyPopper = () => {
  popper.value?.destroy()
  popper.value = undefined
}

const togglePopover = async () => {
  if (props.disabled) {
    closePopover()
    return
  }

  if (show.value) {
    closePopover()
  } else {
    openPopover()
  }
}

const closePopover = () => {
  show.value = false
  destroyPopper()

  if (props.doTeleport) {
    moveToContainer()
  }

  emit('close')
}

const openPopover = async () => {
  show.value = true

  if (popoverTrigger.value && popoverContent.value) {
    if (props.doTeleport) {
      moveToBody()
    }

    if (props.fullWidth) {
      popoverContent.value.style.width = `${popoverTrigger.value?.offsetWidth}px`
    }

    popper.value = new Popper(popoverTrigger.value, popoverContent.value, options.value)

    // We wait for the nextTick update because popoverContent bounding client values
    // are not available when the Popper instance is created. This causes the Popper
    // content to be misplaced. We update the Popper instance position with
    // popper.value.update() once the popoverContent bounding client values are available.
    await nextTick()
    popper.value?.update()
  }

  emit('open')
}

const handleClickOnTrigger = () => {
  if (!props.openOnHover) {
    togglePopover()
  }
}

const handleClickOnPopover = () => {
  if (props.closeOnContentClick) {
    closePopover()
  }
}

const handleMouseEnter = () => {
  if (props.openOnHover && !props.disabled && !show.value) {
    openPopover()
  }
}

const handleMouseLeave = (event: MouseEvent) => {
  if (!props.openOnHover || !show.value) {
    return
  }

  const nextHoveredElement = event.relatedTarget as Node | null

  if (popover.value?.contains(nextHoveredElement) || popoverContent.value?.contains(nextHoveredElement)) {
    return
  }

  closePopover()
}

onClickOutside(popover, () => closePopover(), { ignore: props.doTeleport ? ['.ndsPopover__content'] : [] })

onBeforeUnmount(() => {
  destroyPopper()
  removeTeleportedPopperContent()
})

defineExpose({ openPopover, closePopover, show })
</script>

<style lang="scss" scoped>
.ndsPopover {
  &__content {
    z-index: var(--z-index-popover);
  }
}
</style>
